import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllPackages.graphql';
import { Query, QueryallPackagesArgs } from '../../__generated__/globalTypes';

export const useAllPackagesQuery = (options?: QueryHookOptions<Query, QueryallPackagesArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallPackagesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllPackagesLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallPackagesArgs>
): QueryResult<Query, QueryallPackagesArgs> & {
  getAllPackages: LazyQueryExecFunction<Query, QueryallPackagesArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllPackages, result] = useLazyQuery<Query, QueryallPackagesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllPackages, ...result };
};

export const useAllPackagesPromiseQuery = () => {
  const client = useApolloClient();

  const getAllPackages = useCallback(
    (options?: Omit<QueryOptions<QueryallPackagesArgs, Query>, 'query'>) =>
      client.query<Query, QueryallPackagesArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllPackages };
};
// Suspense Query Hooks
export const useAllPackagesSuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QueryallPackagesArgs>) => {
  const result = useSuspenseQuery<Query, QueryallPackagesArgs>(QUERY, {
    ...options
  });

  return result;
};
